import React from 'react';
import styles from './SidePanel.module.css';
import {ChevronDownIcon, ReplaceIcon, SyncIcon} from './EditorIcons';
import TaskProgress from './TaskProgress';
import TaskList from './TaskList';

// Desktop side panel: collapsible Page details, the Tasks list synced to
// canvas selection, and (when connected) the Jira note.
const SidePanel = ({
    title,
    onTitleChange,
    backgroundThumbnail,
    onReplaceBackground,
    detailsOpen,
    onToggleDetails,
    tasks,
    selectedTaskId,
    onSelectTask,
    jira,
    liveLink,
}) => (
    <aside aria-label="Page panel" className={styles.panel}>
        <section className={styles.detailsSection}>
            <button
                type="button"
                aria-expanded={detailsOpen}
                aria-controls="page-details-panel"
                className={styles.detailsToggle}
                onClick={onToggleDetails}
            >
                Page details
                <ChevronDownIcon style={{transform: detailsOpen ? undefined : 'rotate(-90deg)'}}/>
            </button>
            {detailsOpen && (
                <div id="page-details-panel" className={styles.detailsBody}>
                    <div className={styles.field}>
                        <label htmlFor="side-panel-name" className={styles.fieldLabel}>Name</label>
                        <input
                            id="side-panel-name"
                            type="text"
                            value={title || ''}
                            onChange={onTitleChange}
                            className={styles.nameInput}
                        />
                    </div>
                    <div className={styles.field}>
                        <span className={styles.fieldLabel}>Background</span>
                        <div className={styles.thumbWell}>
                            {backgroundThumbnail && <img src={backgroundThumbnail} alt="" className={styles.thumbImg}/>}
                        </div>
                        <button type="button" className={styles.replaceButton} onClick={onReplaceBackground}>
                            <ReplaceIcon/>Replace background
                        </button>
                    </div>
                    {liveLink && (
                        <div className={styles.field}>
                            <span className={styles.fieldLabel}>Share</span>
                            <p className={styles.liveLinkText}>
                                A read-only link to this page, even for people without an Ovalview account.
                            </p>
                            <div className={styles.liveLinkRow}>
                                <a href={liveLink.url} target="_blank" rel="noopener noreferrer" className={styles.liveLinkOpen}>
                                    Open live link
                                </a>
                                <button type="button" className={styles.liveLinkCopy} onClick={liveLink.onCopy}>
                                    Copy
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </section>

        <section aria-labelledby="tasks-heading" className={styles.tasksSection}>
            <div className={styles.tasksHeader}>
                <h2 id="tasks-heading" className={styles.tasksHeading}>Tasks</h2>
                <span className={styles.tasksCount}>{tasks.length} oval{tasks.length === 1 ? '' : 's'}</span>
            </div>
            <TaskProgress tasks={tasks}/>
            <TaskList tasks={tasks} selectedTaskId={selectedTaskId} onSelectTask={onSelectTask}/>
        </section>

        {jira && (
            <section className={styles.jiraSection}>
                <div className={styles.jiraCard}>
                    <SyncIcon className={styles.jiraIcon}/>
                    <div className={styles.jiraBody}>
                        {jira.hasStory ? (
                            <>
                                <span className={styles.jiraTitle}>Jira connected</span>
                                <span className={styles.jiraText}>
                                    Saving creates a story for this page and a task for each oval.
                                </span>
                                {jira.storyUrl && (
                                    <a href={jira.storyUrl} target="_blank" rel="noopener noreferrer"
                                       className={styles.jiraLink}>
                                        Story {jira.storyKey}
                                    </a>
                                )}
                                {jira.onSync && (
                                    <button type="button" className={styles.jiraSyncButton} onClick={jira.onSync}
                                            disabled={jira.busy}>
                                        Sync
                                    </button>
                                )}
                            </>
                        ) : (
                            <>
                                <span className={styles.jiraTitle}>Jira available</span>
                                <span className={styles.jiraText}>
                                    Enable to create a story for this page and a task for each oval.
                                </span>
                                <button type="button" className={styles.jiraEnableButton} onClick={jira.onEnable}
                                        disabled={jira.busy}>
                                    Enable
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </section>
        )}
    </aside>
);

export default SidePanel;
